/**
 * Browser Notification Service
 * Handles browser notification permissions and displays notifications
 */

export interface NotificationPermission {
  granted: boolean;
  denied: boolean;
  default: boolean;
}

class BrowserNotificationService {
  private permission: NotificationPermission = {
    granted: false,
    denied: false,
    default: false,
  };

  private lastNotificationId: string | null = null;
  private notificationIds: Set<string> = new Set();

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return this.permission;
    }

    if (Notification.permission === 'granted') {
      this.permission = { granted: true, denied: false, default: false };
      return this.permission;
    }

    if (Notification.permission === 'denied') {
      this.permission = { granted: false, denied: true, default: false };
      return this.permission;
    }

    // Request permission
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        this.permission = { granted: true, denied: false, default: false };
      } else if (permission === 'denied') {
        this.permission = { granted: false, denied: true, default: false };
      } else {
        this.permission = { granted: false, denied: false, default: true };
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      this.permission = { granted: false, denied: false, default: true };
    }

    return this.permission;
  }

  /**
   * Check if notification permission is granted
   */
  isPermissionGranted(): boolean {
    return 'Notification' in window && Notification.permission === 'granted';
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!('Notification' in window)) {
      return { granted: false, denied: false, default: false };
    }

    const permission = Notification.permission;
    return {
      granted: permission === 'granted',
      denied: permission === 'denied',
      default: permission === 'default',
    };
  }

  /**
   * Show a browser notification
   */
  showNotification(
    title: string,
    options?: NotificationOptions & { id?: string }
  ): Notification | null {
    if (!this.isPermissionGranted()) {
      console.warn('Notification permission not granted');
      return null;
    }

    try {
      const notificationOptions: NotificationOptions = {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        requireInteraction: false,
        ...options,
      };

      const notification = new Notification(title, notificationOptions);

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        notification.close();
        
        // If there's a URL in options, navigate to it
        if (options?.data?.url) {
          window.location.href = options.data.url;
        }
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  /**
   * Show notification for a new notification item
   */
  showNotificationForItem(notification: {
    _id: string;
    title: string;
    body: string;
    createdAt?: string;
  }): void {
    // Check if we've already shown this notification
    if (this.notificationIds.has(notification._id)) {
      return;
    }

    // Only show if permission is granted
    if (!this.isPermissionGranted()) {
      return;
    }

    // Mark as shown
    this.notificationIds.add(notification._id);
    this.lastNotificationId = notification._id;

    // Show browser notification
    this.showNotification(notification.title, {
      body: notification.body,
      tag: notification._id, // Prevents duplicate notifications
      data: {
        id: notification._id,
        url: window.location.origin + '/notifications/history',
      },
    });
  }

  /**
   * Clear notification tracking (useful when marking notifications as read)
   */
  clearNotificationTracking(notificationId?: string): void {
    if (notificationId) {
      this.notificationIds.delete(notificationId);
    } else {
      this.notificationIds.clear();
    }
  }

  /**
   * Reset last notification ID (useful when loading notifications)
   */
  resetLastNotificationId(): void {
    this.lastNotificationId = null;
  }
}

// Export singleton instance
export const browserNotificationService = new BrowserNotificationService();
export default browserNotificationService;
